/**
 * REAUTH MODAL
 * 
 * Modal de re-autenticación para acceso a datos ultra-sensibles en WEB.
 * Solicita la contraseña antes de mostrar información muy sensible (ej: notas de sesión).
 * 
 * NOTA: El 2FA principal (SMS) se hace al login.
 * Este modal es una capa adicional para datos específicos ultra-sensibles.
 * 
 * Uso:
 * 1. Se muestra cuando el usuario intenta acceder a datos ultra-sensibles
 * 2. El usuario ingresa su contraseña
 * 3. Una vez verificado, tiene acceso durante 5 minutos
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { webAuthGuardService, ReauthResult } from '@/core/security/web-auth-guard.service';

interface ReauthModalProps {
  isOpen: boolean;
  reason: string;
  onComplete: (result: ReauthResult) => void;
}

export const ReauthModal: React.FC<ReauthModalProps> = ({ isOpen, reason, onComplete }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Resetear estado al abrir
      setPassword('');
      setError(null);
      
      // Focus en el input
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Ingresa tu contraseña');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await webAuthGuardService.verifyPassword(password);
      
      if (success) {
        onComplete({ success: true });
      } else {
        setError('Contraseña incorrecta');
        setPassword('');
      }
    } catch (err: any) {
      // Error: 'Reauth error:', err);
      setError(`Error: ${err.message || 'Error al verificar'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onComplete({ success: false, error: 'Cancelado por el usuario' });
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-stone-800 to-stone-900 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h2 className="text-lg font-bold text-white">Verificación requerida</h2>
              <p className="text-sm text-stone-300">{reason}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Contraseña de tu cuenta
              </label>
              <input
                ref={inputRef}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-lg"
                disabled={loading}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">⚠️ {error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              {loading ? '⏳ Verificando...' : '✓ Verificar'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-200">
          <div className="flex items-center justify-between">
            <p className="text-xs text-stone-500">
              🔒 Protección adicional para datos sensibles
            </p>
            <button
              onClick={handleCancel}
              className="text-sm text-stone-500 hover:text-red-600 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ReauthModal;
